import { create } from 'zustand'

import { API } from '@/api'
import { GetAccountRes } from '@/api/type-response'
import { ServerUserInfo } from '@/api/type-server'
import { TEAM_PASSWORD_RESET_CHECK_ENABLED } from '@/consts/features'
import { ORDER_CATALOG, OrderStatus } from '@/consts/order'
import { VOLUME_TYPE } from '@/models/volume'
import { getToken } from '@/utils/auth'

interface UserStoreState {
  id: number
  name: string
  nickname: string
  alias: string
  email: string
  createTime: number
  binds: any
  balance: number
  domains: number[]
  domainServices: { domain: number; services: { label: string; url: string }[] }[]
  domainCurrentServiceMap: Record<number, string>
  quotas: ServerUserInfo['quotas']
  globalSnapVolume: ServerUserInfo['globalSnapVolume']
  partVolume: ServerUserInfo['partVolume']
  expiredOrderUsage: {
    globalSnapUsageMB: number
    partVolumeUsageMB: number
  }
  invitationInfo: {
    invitationCode: string
    totalInvited: number
  }
  account: GetAccountRes['account']
  order: any
  isOrderExpired: boolean
  sshPubKey: string
  privateRegionPermission: boolean
  setPrivateRegionPermission: (hasPermission: boolean) => void
  suggestion: {
    needInitPassword: boolean
    needUpdatePassword: boolean
  }
  getUserInfo: () => Promise<void>
  getAccount: () => Promise<void>
  getOrder: () => Promise<void>
  getVolume: () => Promise<void>
  getSshPubKey: () => Promise<void>
  clearNeedInitPassword: () => void
}

export const useUserStore = create<UserStoreState>((set, get) => ({
  id: 0,
  name: '',
  nickname: '',
  alias: '',
  email: '',
  createTime: 0,
  binds: {},
  domains: [],
  balance: 0,
  domainServices: [],
  domainCurrentServiceMap: {},
  quotas: [],
  globalSnapVolume: {
    usage_mb: 0,
    quota_gb: 0,
    init_quota_gb: 0,
    last_quota_gb: 0,
    type: 0,
    domain: 0,
    id: 0,
  },
  partVolume: {
    usage_mb: 0,
    quota_gb: 0,
    init_quota_gb: 0,
    last_quota_gb: 0,
    type: 0,
    domain: 0,
    id: 0,
  },
  expiredOrderUsage: {
    globalSnapUsageMB: 0,
    partVolumeUsageMB: 0,
  },
  invitationInfo: {
    invitationCode: '',
    totalInvited: 0,
  },
  account: {
    balance: 0,
    expense: {
      amount: 0,
      timeSec: 0,
    },
    timeDiscounts: [],
    name: '',
    totalBonus: {
      timeSec: 0,
      moneyCent: 0,
    },
  },
  privateRegionPermission: false,
  setPrivateRegionPermission(hasPermission: boolean) {
    set(() => ({
      privateRegionPermission: hasPermission,
    }))
  },
  order: undefined,
  isOrderExpired: false,
  sshPubKey: '',
  suggestion: {
    needInitPassword: false,
    needUpdatePassword: false,
  },
  async getUserInfo() {
    const res = await API.getUserInfo()
    const domainServices = res.domainServicesVolume?.map((ds) => ({
      domain: ds.domain,
      services: ds.services['9'].accessableServices?.map((s) => ({
        label: s.label,
        url: s.value,
      })),
    }))

    set(() => ({
      id: res.user.id,
      name: res.user.name,
      nickname: res.user.nickname,
      alias: res.user.alias,
      email: res.user.name,
      createTime: res.user.createTime?.seconds || 0,
      binds: res.binds,
      domains: res.domains.map((ds) => ds.domain),
      partVolume: res.partVolume,
      invitationInfo: res.invitationInfo,
      globalSnapVolume: res.globalSnapVolume,
      suggestion: {
        ...res.suggestion,
        needUpdatePassword: TEAM_PASSWORD_RESET_CHECK_ENABLED && res.suggestion.needUpdatePassword,
      },
      domainServices,
      quotas: res.quotas || [],
      domainCurrentServiceMap: domainServices.reduce<Record<number, string>>(
        (res, { domain, services }) => {
          res[domain] = services?.[0].url
          return res
        },
        {},
      ),
    }))

    // Update team store with scopeInfo if present
    if (res.scopeInfo) {
      const { useTeamStore } = await import('@/stores/team')
      const teamStore = useTeamStore.getState()
      teamStore.setScopeInfo(res.scopeInfo)

      // Sync user's team role
      if (res.user.scopeRole) {
        teamStore.setUserRole(res.user.scopeRole)
      }
    }
  },
  async getAccount() {
    // Don't call API if no valid token available
    // Check both platform token and team token
    const platformToken = getToken()
    const { useTeamStore } = await import('@/stores/team')
    const teamTokenData = useTeamStore.getState().teamTokenData
    const hasValidToken = platformToken || teamTokenData

    if (!hasValidToken) {
      return
    }

    const res = await API.getAccount()
    set(() => ({
      balance: res.account.balance,
      account: res.account,
    }))
  },
  async getOrder() {
    const res = await API.getOrders({
      catalog: ORDER_CATALOG.STORAGE,
      status_filter: OrderStatus.ALL,
    })
    const orders = res?.orders || []
    const order = orders[orders.length - 1]

    // Calculate if order is expired
    const currentTimeSeconds = Math.floor(Date.now() / 1000)
    const isExpired = order?.deadlineTime?.seconds
      ? order.deadlineTime.seconds < currentTimeSeconds
      : false

    set(() => ({
      order: order,
      isOrderExpired: isExpired,
    }))

    // Update volume data after order status changes
    await get().getVolume()
  },
  async getVolume() {
    const res = await API.getVolumes()
    const volumes = res.volumes || []

    // Check if order is expired first
    const {
      isOrderExpired,
      globalSnapVolume: currentGlobalSnapVolume,
      partVolume: currentPartVolume,
    } = useUserStore.getState()

    // Always calculate new volume data from API
    const initial = {
      usage_mb: 0,
      quota_gb: 0,
      init_quota_gb: 0,
      last_quota_gb: 0,
      id: 0,
    }

    const calculatedGlobalSnapVolume = volumes
      .filter((v: any) => v.type === VOLUME_TYPE.SNAPSHOT)
      .reduce(
        (acc: any, v: any) => {
          acc.usage_mb += v.usage_mb || 0
          acc.quota_gb += v.quota_gb || 0
          acc.init_quota_gb += v.init_quota_gb || 0
          acc.last_quota_gb += v.last_quota_gb || 0
          return acc
        },
        {
          ...initial,
          type: VOLUME_TYPE.SNAPSHOT,
          domain: 0,
        },
      )

    const partVolumeUsage = volumes
      .filter((v: any) => v.type !== VOLUME_TYPE.SNAPSHOT)
      .reduce((acc: number, v: any) => acc + (v.usage_mb || 0), 0)

    const calculatedPartVolume = {
      ...currentPartVolume,
      // Only update usage_mb if we get valid data from API, otherwise keep existing
      usage_mb: partVolumeUsage > 0 ? partVolumeUsage : currentPartVolume.usage_mb,
    }

    // Decide whether to override data based on order expiration status
    const globalSnapVolume = isOrderExpired ? calculatedGlobalSnapVolume : currentGlobalSnapVolume
    const partVolume = isOrderExpired ? calculatedPartVolume : currentPartVolume

    // Calculate expired order usage if order is expired
    const expiredOrderUsage = {
      globalSnapUsageMB: isOrderExpired ? globalSnapVolume.usage_mb : 0,
      partVolumeUsageMB: isOrderExpired ? partVolume.usage_mb : 0,
    }

    set(() => ({
      globalSnapVolume,
      partVolume,
      expiredOrderUsage,
    }))
  },
  async getSshPubKey() {
    const res = await API.getUserSshPubKey()
    set(() => ({
      sshPubKey: res.publicKey.key,
    }))
  },
  clearNeedInitPassword() {
    set(() => ({
      suggestion: {
        needInitPassword: false,
        needUpdatePassword: false,
      },
    }))
  },
}))

export const useIsLoggedIn = () => useUserStore((state: UserStoreState) => state.id > 0)
export const useIsOrderExpired = () => useUserStore((state: UserStoreState) => state.isOrderExpired)
export const useExpiredOrderUsage = () =>
  useUserStore((state: UserStoreState) => state.expiredOrderUsage)
export const useSuggestion = () => useUserStore((state: UserStoreState) => state.suggestion)
